import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Spinner, Alert, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast';
import './AdminSubscriptionDashboard.css';

const AdminSubscriptionDashboard = ({ ViewedPage, app_url }) => {
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    shop: '',
    user_email: '',
    page: 1,
    limit: 20
  });

  // Load subscription list once (or on refresh). Do NOT refetch on every search keystroke — that caused
  // slow UX and out-of-order responses showing wrong rows before the correct filter result.
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${app_url}/api/subscriptions/stats`, {
        withCredentials: true
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [app_url]);

  const fetchAllSubscriptions = useCallback(async (showFullPageLoading = true) => {
    if (showFullPageLoading) setSubscriptionsLoading(true);
    try {
      const response = await axios.get(`${app_url}/api/subscriptions/all?page=1&limit=1000`, {
        withCredentials: true
      });
      if (response.data.success) {
        setAllSubscriptions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      if (showFullPageLoading) setSubscriptionsLoading(false);
    }
  }, [app_url]);

  const searchShopNorm = (filters.shop || '').trim().toLowerCase();
  const searchEmailNorm = (filters.user_email || '').trim().toLowerCase();
  const statusFilter = filters.status || '';

  const filteredSubscriptions = useMemo(() => {
    return allSubscriptions.filter((sub) => {
      if (statusFilter && sub.status !== statusFilter) return false;

      if (searchShopNorm) {
        const shop = (sub.shop || '').toLowerCase();
        const shopName = (sub.shop_name || '').toLowerCase();
        if (!shop.includes(searchShopNorm) && !shopName.includes(searchShopNorm)) {
          return false;
        }
      }

      if (searchEmailNorm) {
        const email = (sub.user_email || '').toLowerCase();
        if (!email.includes(searchEmailNorm)) {
          return false;
        }
      }

      return true;
    });
  }, [allSubscriptions, statusFilter, searchShopNorm, searchEmailNorm]);

  // Keep current page valid when filter narrows results (e.g. was page 5, now only 2 pages)
  useEffect(() => {
    const pages = Math.ceil(filteredSubscriptions.length / filters.limit) || 1;
    if (filters.page > pages) {
      setFilters((prev) => ({ ...prev, page: pages }));
    }
  }, [filteredSubscriptions.length, filters.limit, filters.page]);

  const paginationComputed = useMemo(() => {
    const total = filteredSubscriptions.length;
    const limit = filters.limit;
    const pages = Math.ceil(total / limit) || 1;
    const page = Math.min(Math.max(1, filters.page), pages);
    const start = (page - 1) * limit;
    const paginatedSubscriptions = filteredSubscriptions.slice(start, start + limit);
    return { total, pages, page, limit, paginatedSubscriptions };
  }, [filteredSubscriptions, filters.page, filters.limit]);

  useEffect(() => {
    ViewedPage(104);
    fetchAllSubscriptions();
    fetchStats();
  }, [ViewedPage, fetchAllSubscriptions, fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Reset to first page for filter changes, but respect explicit page changes
      page: key === 'page' ? value : 1
    }));
  };

  const handleSearch = () => {
    // Search is applied instantly from filters (client-side); button kept for UX parity.
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      pending: { variant: 'warning', text: 'Pending' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      paused: { variant: 'secondary', text: 'Paused' },
      created: { variant: 'info', text: 'Created' },
      authenticated: { variant: 'primary', text: 'Authenticated' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol}${amount.toLocaleString()}`;
  };

  return (
    <div className="main-container admin-subscription-dashboard-container">
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="mb-0">Subscription Management Dashboard</h2>
            <p className="text-muted">Manage all customer subscriptions</p>
          </Col>
        </Row>

        {/* Statistics Cards */}
        {statsLoading ? (
          <Row className="mb-4">
            <Col className="text-center">
              <Spinner animation="border" />
            </Col>
          </Row>
        ) : stats && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="stat-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Total Subscriptions</h6>
                  <h3 className="mb-0">{stats.total_subscriptions}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Active Subscriptions</h6>
                  <h3 className="mb-0 text-success">{stats.active_subscriptions}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Total Revenue</h6>
                  <h3 className="mb-0">₹{stats.total_revenue.toLocaleString()}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="stat-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Monthly Revenue</h6>
                  <h3 className="mb-0">₹{stats.monthly_revenue.toLocaleString()}</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="paused">Paused</option>
                  <option value="created">Created</option>
                  <option value="authenticated">Authenticated</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Shop</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by shop"
                  value={filters.shop}
                  onChange={(e) => handleFilterChange('shop', e.target.value)}
                />
              </Col>
              <Col md={4}>
                <Form.Label>User Email</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="email"
                    placeholder="Search by user email"
                    value={filters.user_email}
                    onChange={(e) => handleFilterChange('user_email', e.target.value)}
                  />
                  <Button variant="primary" onClick={handleSearch}>
                    Search
                  </Button>
                </InputGroup>
              </Col>
              <Col md={2}>
                <Form.Label>&nbsp;</Form.Label>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => {
                    setFilters({
                      status: '',
                      shop: '',
                      user_email: '',
                      page: 1,
                      limit: 20
                    });
                    fetchAllSubscriptions(false);
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Subscriptions Table - styled similar to Opportunities table */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">All Subscriptions</h5>
          </Card.Header>
          <Card.Body>
            {subscriptionsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : filteredSubscriptions.length === 0 ? (
              <Alert variant="info">No subscriptions found</Alert>
            ) : (
              <>
                <div className="opportunities-keywords-table border rounded bg-white opp-padding">
                  <Table responsive className="mb-0">
                    <thead>
                      <tr>
                        <th className="channel_performance_header_cell">Shop</th>
                        <th className="channel_performance_header_cell">Shop Name</th>
                        <th className="channel_performance_header_cell">User Email</th>
                        <th className="channel_performance_header_cell">Plan Name</th>
                        <th className="channel_performance_header_cell">Price</th>
                        <th className="channel_performance_header_cell">Status</th>
                        <th className="channel_performance_header_cell">Start Date</th>
                        <th className="channel_performance_header_cell">End Date</th>
                        <th className="channel_performance_header_cell">Razorpay ID</th>
                        <th className="channel_performance_header_cell">Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginationComputed.paginatedSubscriptions.map((sub, index) => (
                          <tr
                            key={sub._id}
                            className={index % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}
                          >
                            <td className="channel_performance_table_cell">
                              <code>{sub.shop || 'N/A'}</code>
                            </td>
                            <td className="channel_performance_table_cell">
                              {sub.shop_name || sub.shop || 'N/A'}
                            </td>
                            <td className="channel_performance_table_cell">
                              {sub.user_email}
                            </td>
                            <td className="channel_performance_table_cell">
                              {sub.plan_name}
                            </td>
                            <td className="channel_performance_table_cell">
                              {formatCurrency(sub.plan_price, sub.plan_currency)} / {sub.plan_interval}
                            </td>
                            <td className="channel_performance_table_cell">
                              {getStatusBadge(sub.status)}
                            </td>
                            <td className="channel_performance_table_cell">
                              {formatDate(sub.current_start)}
                            </td>
                            <td className="channel_performance_table_cell">
                              {formatDate(sub.current_end)}
                            </td>
                            <td className="channel_performance_table_cell">
                              <code className="small">{sub.razorpay_subscription_id || 'N/A'}</code>
                            </td>
                            <td className="channel_performance_table_cell">
                              {formatDate(sub.createdAt)}
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {paginationComputed.pages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing {((paginationComputed.page - 1) * paginationComputed.limit) + 1} to{' '}
                      {Math.min(paginationComputed.page * paginationComputed.limit, paginationComputed.total)} of{' '}
                      {paginationComputed.total} subscriptions
                    </div>
                    <div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={paginationComputed.page === 1}
                        onClick={() => handleFilterChange('page', paginationComputed.page - 1)}
                      >
                        Previous
                      </Button>
                      <span className="mx-2">
                        Page {paginationComputed.page} of {paginationComputed.pages}
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={paginationComputed.page === paginationComputed.pages}
                        onClick={() => handleFilterChange('page', paginationComputed.page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AdminSubscriptionDashboard;
